import { PayProAPI } from './paypro-api-model';

/**
 * The read-only payment and refund history support sees (#4248 Phase 5).
 *
 * When a customer writes in about money — "was I charged twice?", "where is my
 * refund?" — support had to open the payment provider's dashboard to answer.
 * This turns each provider's own shape into ONE list of rows so both front-ends
 * render the same history and cannot drift.
 *
 * **Nothing here issues a refund, and nothing in this phase can.** The tooling
 * shows charges, refunds and chargebacks; giving money back stays in the
 * provider's dashboard. That is a deliberate decision, and it is also why the
 * deployed Paddle key deliberately lacks the `adjustment.write` scope.
 */

/** What a history row actually represents. Kept separate from the provider's own
 *  status text, which we also show verbatim. */
export enum PaymentHistoryKind {
  /** Money successfully taken from the customer. */
  Payment = 'payment',
  /** Money we gave back. */
  Refund = 'refund',
  /** The customer went to their bank instead of to us. Deliberately NOT folded
   *  in with refunds: it costs a fee and is a support signal of its own. */
  Chargeback = 'chargeback',
  /** Started but not settled. */
  Pending = 'pending',
  /** Never went through. */
  Canceled = 'canceled',
  /** A status we do not recognise. Reported as-is rather than assumed to be a
   *  successful payment — telling support the customer paid when we do not
   *  actually know that is the worst of the available guesses. */
  Unknown = 'unknown',
}

/** One row of payment history, normalized across providers. */
export interface PaymentHistoryEntry {
  /** When it happened, as the provider reports it, shown verbatim.
   *
   *  NOTE the `_utc` suffix is aspirational for PayPro: we request its
   *  `dateFormat: "a"` (`yyyy-MM-ddTHH:mm:ss.fff`), which carries no timezone.
   *  `sortNewestFirst` pins such a value to UTC for its SORT KEY — which it has
   *  to, because one list can hold both a zone-less PayPro row and a `Z`-suffixed
   *  Paddle one — while the DISPLAYED string stays exactly as the provider sent
   *  it. Do not build timezone-sensitive arithmetic on this field without
   *  pinning the zone yourself. */
  date_utc?: string;
  /** The amount in MAJOR units (what a human would say), as a plain string —
   *  deliberately not re-formatted, so an operator sees the same number the
   *  provider's own dashboard shows them. */
  amount?: string;
  /** ISO currency code, e.g. `EUR`. */
  currency?: string;
  /** The provider's own status wording, shown verbatim. */
  status?: string;
  kind: PaymentHistoryKind;
  /** The provider-side id (order id / transaction id) so support can quote it. */
  reference?: string;
  /** True for a sandbox / test-mode row, so it is never mistaken for real money. */
  isTest?: boolean;
}

/**
 * Map a PayPro order status id to what the row means.
 * PayPro's documented ids: 1 Waiting, 2 Canceled, 3 Refunded, 4 Chargeback,
 * 5 Processed.
 */
export function classifyPayProOrderStatus(orderStatusId: number | undefined): PaymentHistoryKind {
  switch (orderStatusId) {
    case 5: return PaymentHistoryKind.Payment;
    case 3: return PaymentHistoryKind.Refund;
    case 4: return PaymentHistoryKind.Chargeback;
    case 1: return PaymentHistoryKind.Pending;
    case 2: return PaymentHistoryKind.Canceled;
    default: return PaymentHistoryKind.Unknown;
  }
}

/**
 * Build the history rows from the PayPro subscription details the admin callable
 * ALREADY fetches (it walks `orders` and calls `Orders/GetOrderDetails` for
 * each). No extra API call — this data was being thrown away.
 *
 * Pure, and tolerant of a malformed order: one bad row must not cost support the
 * whole history.
 *
 * **A refund reads differently here than on the Paddle rail, and it is worth
 * knowing before quoting a number to a customer.** PayPro does not add a row for
 * a refund — it MUTATES the original order's status to Refunded. So a refunded
 * customer shows one `** REFUND **` line and NO separate charge line, and the
 * amount on it is the original order total. For a PARTIAL refund that overstates
 * what was actually returned. (Paddle, by contrast, adds a separate adjustment
 * row carrying the real refunded amount.) Fixing this would need PayPro's
 * per-order refund detail, which `GetOrderDetails` does not break out.
 */
export function buildPayProPaymentHistory(
  details: PayProAPI.GetSubscriptionDetailsRequestResponse | undefined,
): PaymentHistoryEntry[] {
  const orders = details?.orders ?? [];
  const entries = orders.map(order => ({
    date_utc: order?.createdAt,
    // the with-taxes total is what actually came off the customer's card;
    // billingTotalPrice excludes tax and would understate every row.
    amount: order?.billingTotalPriceWithTaxes !== undefined
      ? String(order.billingTotalPriceWithTaxes)
      : undefined,
    currency: order?.billingCurrencyCode,
    status: order?.orderStatusName,
    kind: classifyPayProOrderStatus(order?.orderStatusId),
    reference: order?.id !== undefined ? String(order.id) : undefined,
    isTest: order?.orderDetails?.isTestMode === true ? true : undefined,
  }));
  return sortNewestFirst(entries);
}

/** Newest first — the most recent charge is what support is almost always
 *  asking about. Rows with no usable date sink to the bottom rather than
 *  jumping to the top. */
export function sortNewestFirst(entries: PaymentHistoryEntry[]): PaymentHistoryEntry[] {
  return [...entries].sort((a, b) => dateSortKey(b) - dateSortKey(a));
}

function dateSortKey(entry: PaymentHistoryEntry): number {
  if (!entry.date_utc) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(normalizeToUtcForSorting(entry.date_utc));
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/**
 * Make a provider timestamp sort as UTC.
 *
 * This matters because one list can now hold rows from BOTH providers on a
 * provider-migration account: Paddle sends a `Z`-suffixed UTC time, while PayPro
 * sends a zone-less `yyyy-MM-ddTHH:mm:ss.fff` that `Date.parse` would read as the
 * SERVER's local time. Left alone, the two interleave wrongly by the host's UTC
 * offset — so a refund could sort above the charge it refunded.
 *
 * Only the SORT KEY is normalized; the displayed string stays exactly as the
 * provider sent it, because the operator is reconciling against that provider's
 * own dashboard.
 */
function normalizeToUtcForSorting(raw: string): string {
  // already carries a zone (trailing Z, or a ±hh:mm offset after the time)
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(raw)) return raw;
  // date-and-time with no zone → pin it to UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return `${raw}Z`;
  return raw;
}

/**
 * Render the rows as plain text lines, shared by the admin page and the CLI so
 * the two cannot describe the same history differently.
 *
 * Refunds, chargebacks and test rows are shouted in capitals: an operator
 * scanning a list of near-identical money lines needs the exceptional ones to
 * be impossible to skim past.
 */
export function formatPaymentHistoryLines(entries: PaymentHistoryEntry[]): string[] {
  return entries.map(entry => {
    const parts: string[] = [];
    parts.push(entry.date_utc ?? '(date unknown)');
    parts.push(entry.amount !== undefined && entry.amount !== ''
      ? `${entry.amount} ${entry.currency ?? ''}`.trim()
      : '(amount unknown)');
    const marker = kindMarker(entry.kind);
    if (marker) {
      parts.push(marker);
    }
    parts.push(entry.status ?? '(status unknown)');
    if (entry.reference) {
      parts.push(`ref=${entry.reference}`);
    }
    if (entry.isTest) {
      parts.push('[TEST]');
    }
    return parts.join('  ');
  });
}

/** The shouted label for the rows that must not be skimmed past. An ordinary
 *  successful payment gets no marker — marking everything marks nothing. */
function kindMarker(kind: PaymentHistoryKind): string | undefined {
  switch (kind) {
    case PaymentHistoryKind.Refund: return '** REFUND **';
    case PaymentHistoryKind.Chargeback: return '** CHARGEBACK **';
    case PaymentHistoryKind.Canceled: return '(canceled)';
    case PaymentHistoryKind.Pending: return '(pending)';
    case PaymentHistoryKind.Unknown: return '(status not recognised)';
    default: return undefined;
  }
}
